import { Drawer, Loader, Text } from '@southfields-digital/mpxlive-components';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Socket } from 'socket.io-client';

import ActiveGraphics, { ActiveGraphicsHeader } from 'src/components/ActiveGraphics';
import { StatusDescription } from 'src/components/GoLive';
import { getMappedCanvases } from 'src/components/GoLive/utils';
import GraphicDetails from 'src/components/GraphicDetails';
import DisplayHubDetails from 'src/components/DisplayHubDetails';
import GraphicList from 'src/components/GraphicList/GraphicList';
import Graphics from 'src/components/Graphics';
import DisplayHubs from 'src/components/DisplayHubs';
import BundledGraphics from 'src/components/BundledGraphics';
import MatchControl from 'src/components/MatchControl';
import RundownDialog, { DialogTypes } from 'src/components/RundownDialog';
import Playlists from 'src/components/Playlists';
import QuickControl from 'src/components/QuickControl/QuickControl';
import useAuth from 'src/hooks/useAuth';
import { LayoutDisplayHub } from './Layouts';
import {
  PanelGroup,
  Panel,
  PanelResizeHandle as ResizeHandle,
  PanelResizeHandle,
} from 'src/components/ResizeablePanel';
import RundownLocked from 'src/components/RundownLocked';
import ToggleButtons from 'src/components/ToggleButtons/ToggleButtons';
import ViewWindow from 'src/components/ViewWindow/ViewWindow';
import { injectBaseLayoutContext, InjectedBaseLayoutContextProps } from 'src/layouts/BaseLayout';
import { StateType } from 'src/redux/reducers';
import {
  REQUEST_GET_MATCH_CONTROL,
  REQUEST_GET_MATCH_CONTROL_CONTROLS,
} from 'src/redux/reducers/matchControls';
import {
  REQUEST_CREATE_PLAYLIST,
  REQUEST_GET_ALL_PLAYLISTS_BY_RUNDOWN_ID,
} from 'src/redux/reducers/playlist';
import {
  REQUEST_CREATE_PLAYLIST_GRAPHIC,
  REQUEST_GET_ALL_PLAYLIST_GRAPHICS,
} from 'src/redux/reducers/playlistGraphic';
import { actions as bundledPlaylistGraphicActions } from 'src/redux/reducers/bundledPlaylistGraphic';
import {
  REQUEST_CREATE_PLAYLIST_DISPLAY_HUB,
  REQUEST_GET_ALL_PLAYLIST_DISPLAY_HUBS,
} from 'src/redux/reducers/playlistDisplayHub';
import {
  REQUEST_GET_RUNDOWN,
  REQUEST_UPDATE_RUNDOWN_OPERATOR,
  RundownDetailStateType,
  UPDATE_CURRENT_RUNDOWN,
} from 'src/redux/reducers/rundown';
import { WebsocketBuddy } from 'src/services/websockets';
import { NOTIFY } from 'src/redux/reducers/notification';
import { Variant } from 'src/components/Toast/types';
import { REQUEST_CREATE_PLAYLIST_DISPLAY_HUB_STORY } from 'src/redux/reducers/playlistDisplayHubStory';
import { useRundownLayout } from 'src/components/RundownSettings/useRundownLayout';
import {
  ISocketMatchControlProps,
  ISocketPlaylistGraphicProps,
  ISocketPlaylistProps,
  ISocketRundownProps,
} from 'src/types/socket/socket';

import styles from './RundownDetail.module.scss';

function RundownDetail({
  baseLayoutContext: {
    setCurrentlyOpenRundownId,
    setFullWidth,
    setDisplayClock,
    setGoLive,
    setShowRundownName,
    setShowRundownSettings,
  },
}: InjectedBaseLayoutContextProps) {
  const socketInstance = useRef<Socket | null>(null);
  const { rundownId } = useParams<{ rundownId: string }>();
  const { layout } = useRundownLayout();
  const { isBetaUser } = useAuth();

  const dispatch = useDispatch();

  const { data, outputs, loading, error }: RundownDetailStateType = useSelector(
    (state: StateType) => state.rundown.detail
  );

  const { selectedId: activePlaylistId } = useSelector((state: StateType) => state.playlist.getAll);
  const { selectedPlaylistGraphic } = useSelector((state: StateType) => state.playlistGraphic);
  const { selectedPlaylistDisplayHub } = useSelector(
    (state: StateType) => state.playlistDisplayHub
  );
  const { bundledPlaylistGraphics } = useSelector(
    (state: StateType) => state.bundledPlaylistGraphic
  );

  // Current activePlaylistId is volatile and likely to change during API requests, so define a ref for use in callbacks
  const guaranteedActivePlaylistId = useRef<string | null>(activePlaylistId);

  const [selectedCanvas, setSelectedCanvas] = useState<RundownTemplateCanvas | undefined>();
  const [showMatchControl, setShowMatchControl] = useState<boolean>(false);
  const [showBundledGraphics, setShowBundledGraphics] = useState<boolean>(false);
  const [showDisplayHub, setShowDisplayHub] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<DialogTypes | null>(null);
  const [showGraphicDrawer, setShowGraphicDrawer] = useState<boolean>(false);
  const [overwriteLockedWindow, setOverwriteLockedWindow] = useState<boolean>(false);

  const getRundown = (rundownId: string | null) => {
    dispatch({ type: REQUEST_GET_RUNDOWN, payload: { id: rundownId } });
  };

  const getRundownGraphics = (rundownId: string) => {
    dispatch({ type: REQUEST_GET_ALL_PLAYLIST_GRAPHICS, payload: { rundownId } });
  };

  const getBundledGraphics = (rundownId: string) => {
    dispatch(bundledPlaylistGraphicActions.requestGetBundledPlaylistGraphics({ rundownId }));
  };

  const getRundownDisplayHubs = (rundownId: string) => {
    dispatch({ type: REQUEST_GET_ALL_PLAYLIST_DISPLAY_HUBS, payload: { rundownId } });
  };

  const handleSocketRundown = (_: ISocketRundownProps) => {
    dispatch({
      type: UPDATE_CURRENT_RUNDOWN,
      payload: { id: rundownId },
    });
  };

  const handleSocketPlaylist = (_: ISocketPlaylistProps) => {
    dispatch({
      type: REQUEST_GET_ALL_PLAYLISTS_BY_RUNDOWN_ID,
      payload: { rundownId, resetSelected: false },
    });
  };

  const handleSocketPlaylistGraphic = (_: ISocketPlaylistGraphicProps) => {
    if (rundownId) {
      getRundownGraphics(rundownId);
      getBundledGraphics(rundownId);
    }
  };

  const handleSocketPlaylistDisplayHub = (_: ISocketPlaylistGraphicProps) => {
    if (rundownId) {
      getRundownDisplayHubs(rundownId);
    }
  };

  const handleSocketMatchControl = (data: ISocketMatchControlProps) => {
    dispatch({ type: REQUEST_GET_MATCH_CONTROL, payload: { rundownId } });
    dispatch({ type: REQUEST_GET_MATCH_CONTROL_CONTROLS, payload: { rundownId } });

    if (data.clockType === 'penaltyclock') {
      const variant: Variant = data.clockBaseTime <= 120000 ? 'success' : 'warning';
      dispatch({ type: NOTIFY, payload: { variant, title: 'Penalty clock added' } });
    }
  };

  const handleUpdateRundownOperator = () => {
    dispatch({ type: REQUEST_UPDATE_RUNDOWN_OPERATOR, payload: { id: rundownId } });
  };

  const getDialogTitle = () => {
    switch (showDialog) {
      case DialogTypes.PLAYLIST:
        return 'Create Playlist';
      case DialogTypes.DISPLAY_HUB:
        return 'Create Display Hub';
      case DialogTypes.DISPLAY_HUB_STORY:
        return 'Create Display Hub Story';
      default:
        return '';
    }
  };

  const getDialogSubtitle = () => {
    switch (showDialog) {
      case DialogTypes.PLAYLIST:
        return 'Enter name for the playlist';
      case DialogTypes.DISPLAY_HUB:
        return 'Enter name for the display hub';
      case DialogTypes.DISPLAY_HUB_STORY:
        return 'Enter name for the display hub story';
      default:
        return '';
    }
  };

  const handleRundownDialog = (name: string) => {
    switch (showDialog) {
      case DialogTypes.PLAYLIST:
        handleNewPlaylist(name);
        break;
      case DialogTypes.DISPLAY_HUB:
        handleNewDisplayHub(name);
        break;
      case DialogTypes.DISPLAY_HUB_STORY:
        handleNewDisplayHubStory(name);
        break;
      default:
        break;
    }
  };

  const handleNewPlaylist = (name: string) => {
    dispatch({ type: REQUEST_CREATE_PLAYLIST, payload: { rundownId, name } });
    setShowDialog(null);
  };

  const handleNewDisplayHub = (name: string) => {
    dispatch({
      type: REQUEST_CREATE_PLAYLIST_DISPLAY_HUB,
      payload: { playlistId: guaranteedActivePlaylistId.current, name },
    });

    setShowDialog(null);
  };

  const handleNewDisplayHubStory = (name: string) => {
    if (!selectedPlaylistDisplayHub) return;

    dispatch({
      type: REQUEST_CREATE_PLAYLIST_DISPLAY_HUB_STORY,
      payload: { rundownId, playlistDisplayHubId: selectedPlaylistDisplayHub.id, name },
    });

    setShowDialog(null);
  };

  const handleGraphicSelected = (graphicId: string) => {
    dispatch({
      type: REQUEST_CREATE_PLAYLIST_GRAPHIC,
      payload: { playlistId: guaranteedActivePlaylistId.current, graphicId },
    });

    setShowGraphicDrawer(false);
  };

  useEffect(() => {
    if (!rundownId) return;
    getRundown(rundownId);
    getRundownGraphics(rundownId);
    getBundledGraphics(rundownId);
    getRundownDisplayHubs(rundownId);
  }, [rundownId, dispatch]);

  useEffect(() => {
    guaranteedActivePlaylistId.current = activePlaylistId;
  }, [activePlaylistId]);

  useEffect(() => {
    setCurrentlyOpenRundownId(rundownId);
    setFullWidth(true);
    setGoLive(true);
    setShowRundownName(true);
    setShowRundownSettings(true);
    setDisplayClock(true);

    return () => {
      setCurrentlyOpenRundownId(undefined);
      setFullWidth(false);
      setGoLive(false);
      setDisplayClock(false);
      setShowRundownName(false);
      setShowRundownSettings(false);

      if (socketInstance.current) {
        socketInstance.current.disconnect();
        socketInstance.current = null;
      }

      getRundown(null);
    };
  }, []);

  useEffect(() => {
    if (!data) return;
    if (data.isLive && !selectedCanvas) {
      const activeCanvas = data.canvases.find((canvas) => canvas.output && canvas.graphics.length);
      if (activeCanvas) {
        setSelectedCanvas(activeCanvas);
      }
    }

    if (data.sandboxChannelId && !socketInstance.current) {
      const { socket } = WebsocketBuddy({
        channel: data.sandboxChannelId,
        driver: 'SocketIo',
        listeners: {
          rundown: handleSocketRundown,
          playlist: handleSocketPlaylist,
          playlistGraphic: handleSocketPlaylistGraphic,
          playlistDisplayHub: handleSocketPlaylistDisplayHub,
          matchControl: handleSocketMatchControl,
        },
      });

      socketInstance.current = socket;
    }
  }, [data]);

  useEffect(() => {
    if (!data) return;
    const showHeaders = !data.isLocked || overwriteLockedWindow;
    setGoLive(showHeaders && !overwriteLockedWindow);
    setShowRundownName(showHeaders);
    setShowRundownSettings(showHeaders);
    setDisplayClock(showHeaders);
  }, [data?.isLocked, overwriteLockedWindow]);

  if (!rundownId || loading) return <Loader centeredFullscreen />;
  if (error) return <div className={styles.CenteredContent}>{error}</div>;
  if (!data) return <div className={styles.CenteredContent}>No Rundown found</div>;
  if (data.isLocked && data.usedBy && !overwriteLockedWindow) {
    return (
      <RundownLocked
        data={data.usedBy}
        handleTakeOver={handleUpdateRundownOperator}
        handleAssistant={() => setOverwriteLockedWindow(true)}
      />
    );
  }

  if (layout === 'matchcontrol') {
    return (
      <div className="w-[685px] h-full mx-auto [&>div]:bg-elevated-1">
        <PanelGroup direction="vertical">
          <Panel defaultSize={70}>
            <MatchControl rundownId={rundownId} variant="extended" />
          </Panel>
          <PanelResizeHandle />
          <Panel defaultSize={30}>
            {/* @TO-DO: check if we can remove this key */}
            <QuickControl key={Math.random()} />
          </Panel>
        </PanelGroup>
      </div>
    );
  }

  if (layout === 'quickcontrol') {
    return (
      <div className="w-[685px] h-full mx-auto [&>div]:bg-elevated-1">
        <PanelGroup direction="vertical">
          <Panel defaultSize={42}>
            {/* @TO-DO: check if we can remove this key */}
            <QuickControl key={Math.random()} />
          </Panel>
        </PanelGroup>
      </div>
    );
  }

  if (layout === 'playlist') {
    return (
      <div className="w-[685px] h-full mx-auto [&>div]:bg-elevated-1">
        <PanelGroup direction="vertical">
          <Playlists rundownId={rundownId} view="select" />
          <Panel defaultSize={42}>
            <Graphics />
          </Panel>
        </PanelGroup>
      </div>
    );
  }

  if (layout === 'displayhub') {
    return (
      <div className="h-full">
        <RundownDialog
          dialogTitle={getDialogTitle()}
          dialogSubtitle={getDialogSubtitle()}
          isOpen={!!showDialog}
          onClose={() => setShowDialog(null)}
          onSubmit={handleRundownDialog}
        />
        <LayoutDisplayHub
          rundown={data}
          selectedPlaylistDisplayHub={selectedPlaylistDisplayHub}
          setShowDialog={setShowDialog}
        />
      </div>
    );
  }

  return (
    <div className="h-full">
      <RundownDialog
        dialogTitle={getDialogTitle()}
        dialogSubtitle={getDialogSubtitle()}
        isOpen={!!showDialog}
        onClose={() => setShowDialog(null)}
        onSubmit={handleRundownDialog}
      />
      <PanelGroup className={styles.Container} direction="horizontal">
        <Panel defaultSize={25} minSize={25} id="first-column" order={1}>
          <PanelGroup direction="vertical">
            <Panel
              header="Playlists"
              icon="plus"
              onClick={() => setShowDialog(DialogTypes.PLAYLIST)}
              defaultSize={40}
            >
              <Playlists rundownId={rundownId} />
            </Panel>

            <ResizeHandle />

            <Panel
              header={
                <ToggleButtons
                  items={[
                    { id: 'graphics', name: 'Graphics' },
                    ...(isBetaUser() ? [{ id: 'displayHubs', name: 'Display Hubs' }] : []),
                  ]}
                  selectedItem={
                    !showDisplayHub
                      ? { id: 'graphics', name: 'Graphics' }
                      : { id: 'displayHubs', name: 'Display Hubs' }
                  }
                  onClickItem={(item) => setShowDisplayHub(item.id === 'displayHubs')}
                />
              }
              canEdit={!!activePlaylistId}
              icon="plus"
              onClick={() =>
                showDisplayHub ? setShowDialog(DialogTypes.DISPLAY_HUB) : setShowGraphicDrawer(true)
              }
            >
              {showDisplayHub ? <DisplayHubs /> : <Graphics />}
            </Panel>
          </PanelGroup>
        </Panel>

        <ResizeHandle />

        <Panel defaultSize={50} id="second-column" order={2}>
          <PanelGroup direction="vertical">
            <Panel className="!bg-default-surface">
              <ViewWindow name="Sandbox" src={data.sandboxUrl} liveMode={false} bgSwitch />
            </Panel>

            <ResizeHandle />

            <Panel>
              {selectedPlaylistGraphic && <GraphicDetails key={rundownId} />}
              {selectedPlaylistDisplayHub && (
                <DisplayHubDetails
                  key={rundownId}
                  handleCreateStory={() => setShowDialog(DialogTypes.DISPLAY_HUB_STORY)}
                />
              )}
            </Panel>
          </PanelGroup>
        </Panel>

        {layout !== 'simplified' ? (
          <>
            <ResizeHandle />

            <Panel defaultSize={25} maxSize={50} id="third-column" order={3}>
              <PanelGroup direction="vertical">
                <StatusDescription
                  rundown={data}
                  mappedCanvases={getMappedCanvases(data.canvases, outputs)}
                  isLive={data.isLive}
                  className="border-b-[4px] border-[#151621]"
                />
                {data.isLive && (
                  <>
                    <Panel
                      id="canvas"
                      order={1}
                      defaultSize={37}
                      header={
                        <ToggleButtons
                          items={data.canvases.filter((canvas) => canvas.graphics.length)}
                          selectedItem={selectedCanvas}
                          onClickItem={(canvas) => setSelectedCanvas(canvas)}
                        />
                      }
                    >
                      {selectedCanvas?.output?.liveUrl ? (
                        <ViewWindow name="Live" src={selectedCanvas.output.liveUrl} liveMode />
                      ) : (
                        <div className="flex justify-center items-center h-full">
                          <Text as="p" className="!text-sm !text-gray-300 uppercase p-2">
                            Configure an output for this canvas
                          </Text>
                        </div>
                      )}
                    </Panel>
                    <ResizeHandle />
                  </>
                )}
                <Panel
                  id="quickControls"
                  order={2}
                  defaultSize={data.isLive ? 32.25 : 35}
                  header={
                    <ToggleButtons
                      items={[
                        { id: 'quickControls', name: 'Quick Controls' },
                        {
                          id: 'matchControls',
                          name: 'Match Control',
                          visible: Boolean(data.matchControl),
                        },
                        {
                          id: 'bundledGraphics',
                          name: 'Bundle Controls',
                          visible: Boolean(bundledPlaylistGraphics?.length),
                        },
                      ]}
                      selectedItem={
                        showMatchControl
                          ? { id: 'matchControls', name: 'Match Control' }
                          : showBundledGraphics
                            ? { id: 'bundledGraphics', name: 'Bundle Controls' }
                            : { id: 'quickControls', name: 'Quick Controls' }
                      }
                      onClickItem={(item) => {
                        setShowMatchControl(item.id === 'matchControls');
                        setShowBundledGraphics(item.id === 'bundledGraphics');
                      }}
                    />
                  }
                >
                  {showMatchControl ? (
                    <MatchControl rundownId={rundownId} />
                  ) : showBundledGraphics ? (
                    <BundledGraphics />
                  ) : (
                    <QuickControl />
                  )}
                </Panel>

                <ResizeHandle />

                <Panel
                  header={<ActiveGraphicsHeader />}
                  id="activeGraphics"
                  order={3}
                  overflow={false}
                >
                  <ActiveGraphics />
                </Panel>
              </PanelGroup>
            </Panel>
          </>
        ) : null}
      </PanelGroup>

      <Drawer
        isVisible={showGraphicDrawer}
        onClose={() => setShowGraphicDrawer(false)}
        header="Graphics"
        className="overflow-auto h-full pb-4"
      >
        <GraphicList rundownId={rundownId} handleGraphicSelected={handleGraphicSelected} />
      </Drawer>
    </div>
  );
}

export default injectBaseLayoutContext(RundownDetail);
